import { retrieveMatterPhases } from "@/lib/data/requests/matters/matter-phase";
import { matterPhasesQueryOptions } from "@/lib/data/react-query/matters/matter-phase";
import { queryKeys } from "@/lib/data/static";
import {
  VITE_FAST_API_URL,
  VITE_MATTER_STREAM_URL,
} from "@/lib/data/static/importEnv";
import { fetchAuthSession } from "@aws-amplify/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { createMatterPhaseLiveUpdatesController } from "./controller";
import type { DurableLiveUpdateStatus } from "../controller";

const matterPhaseQueryKey = (matterId: string) =>
  [queryKeys.matterPhases, matterId] as const;

export const useMatterPhaseLiveUpdates = (matterId: string) => {
  const queryClient = useQueryClient();
  const { data } = useQuery(matterPhasesQueryOptions(matterId));
  const [status, setStatus] = useState<DurableLiveUpdateStatus>("connecting");
  const controllerRef =
    useRef<ReturnType<typeof createMatterPhaseLiveUpdatesController>>();
  const reconnect = useCallback(
    () => controllerRef.current?.reconnectNow(),
    [],
  );
  const initialCursor = data?.pagination?.liveUpdateCursor;
  const bootstrapCursor = useRef<{
    cursor: string;
    matterId: string;
  } | null>(null);

  if (bootstrapCursor.current?.matterId !== matterId) {
    bootstrapCursor.current = null;
  }
  if (!bootstrapCursor.current && initialCursor) {
    bootstrapCursor.current = { cursor: initialCursor, matterId };
  }
  const connectionCursor = bootstrapCursor.current?.cursor;

  useEffect(() => {
    if (!connectionCursor) return;
    const websocketBaseUrl = VITE_MATTER_STREAM_URL || VITE_FAST_API_URL;
    if (typeof websocketBaseUrl !== "string" || !websocketBaseUrl) {
      setStatus("degraded");
      return;
    }

    const controller = createMatterPhaseLiveUpdatesController({
      createSocket: (url, protocols) => new WebSocket(url, protocols),
      getAccessToken: async (forceRefresh) => {
        const session = await fetchAuthSession(
          forceRefresh ? { forceRefresh: true } : undefined,
        );
        const accessToken = session.tokens?.accessToken.toString();
        if (!accessToken) throw new Error("Missing Cognito access token");
        return accessToken;
      },
      initialCursor: connectionCursor,
      matterId,
      onChanges: async (_signal, changes) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [queryKeys.matters, "user-tasks"],
          }),
          queryClient.invalidateQueries(
            {
              queryKey: matterPhaseQueryKey(matterId),
            },
            { throwOnError: true },
          ),
          queryClient.invalidateQueries(
            {
              queryKey: [queryKeys.matters, matterId],
            },
            { throwOnError: true },
          ),
        ]);
        if (
          changes?.some((change) => change.operation === "widget_data_changed")
        ) {
          await queryClient.invalidateQueries(
            {
              queryKey: [queryKeys.matterSettings],
            },
            { throwOnError: true },
          );
        }
      },
      onStatusChange: setStatus,
      refreshSnapshot: async (signal) => {
        const refreshed = await queryClient.fetchQuery({
          queryKey: matterPhaseQueryKey(matterId),
          queryFn: () => retrieveMatterPhases(matterId),
          staleTime: 0,
          retry: false,
        });
        if (signal.aborted) throw new DOMException("Aborted", "AbortError");
        await queryClient.invalidateQueries(
          {
            queryKey: [queryKeys.matters, matterId],
          },
          { throwOnError: true },
        );
        await queryClient.invalidateQueries(
          {
            queryKey: [queryKeys.matterSettings],
          },
          { throwOnError: true },
        );
        await queryClient.invalidateQueries(
          { queryKey: [queryKeys.matters, "user-tasks"] },
          { throwOnError: true },
        );
        const cursor = refreshed.pagination?.liveUpdateCursor;
        if (!cursor) {
          throw new Error(
            "The Matter Phase snapshot omitted its stream cursor.",
          );
        }
        return { cursor };
      },
      websocketBaseUrl,
    });

    controllerRef.current = controller;
    controller.start();
    const reconnectWhenOnline = () => {
      controller.reconnectNow();
    };
    window.addEventListener("online", reconnectWhenOnline);
    return () => {
      window.removeEventListener("online", reconnectWhenOnline);
      controller.stop();
      controllerRef.current = undefined;
    };
  }, [connectionCursor, matterId, queryClient]);

  return { status, reconnect };
};
